import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Motion";
import { api, BRAND } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-sm border border-border bg-white px-4 py-3 text-ink outline-none transition-shadow focus:ring-2 focus:ring-tide focus:border-tide";

  return (
    <div>
      <section className="pt-32 md:pt-40 pb-14 bg-secondary grain relative">
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">Contact</span>
            <h1 className="mt-4 font-heading font-extrabold tracking-tighter text-5xl md:text-6xl text-ink leading-[1.05]">Let's get your space spotless.</h1>
            <p className="mt-5 text-ink/60 text-lg max-w-2xl">Questions, quotes or bookings — reach out and a real person from our GTA team will respond.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-[1fr_1.2fr] gap-14">
        {/* Info */}
        <Reveal>
          <div className="space-y-8">
            <div className="space-y-5">
              <a href={BRAND.phoneHref} data-testid="contact-phone" className="flex items-start gap-4 group">
                <div className="h-12 w-12 rounded-sm bg-tide/10 text-tide flex items-center justify-center group-hover:bg-tide group-hover:text-white transition-colors"><Phone size={22} /></div>
                <div><div className="text-sm text-ink/50">Call us</div><div className="font-heading font-bold text-lg text-ink">{BRAND.phone}</div></div>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
                <div className="h-12 w-12 rounded-sm bg-tide/10 text-tide flex items-center justify-center group-hover:bg-tide group-hover:text-white transition-colors"><Mail size={22} /></div>
                <div><div className="text-sm text-ink/50">Email us</div><div className="font-heading font-bold text-lg text-ink break-all">{BRAND.email}</div></div>
              </a>
            </div>

            <div className="rounded-sm border border-border bg-white p-6">
              <div className="flex items-center gap-2 text-ink font-heading font-bold"><Clock size={18} className="text-tide" /> Business Hours</div>
              <ul className="mt-4 space-y-2.5">
                {BRAND.hours.map((h) => (
                  <li key={h.d} className="flex justify-between text-ink/70"><span>{h.d}</span><span className="font-medium text-ink">{h.h}</span></li>
                ))}
              </ul>
            </div>

            <div className="rounded-sm border border-border bg-white p-6">
              <div className="flex items-center gap-2 text-ink font-heading font-bold"><MapPin size={18} className="text-tide" /> Service Area</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {BRAND.areas.map((a) => (
                  <span key={a} className="rounded-sm bg-secondary border border-border px-3 py-1.5 text-sm text-ink/75">{a}</span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.12}>
          <form onSubmit={submit} data-testid="contact-form" className="rounded-sm border border-border bg-white p-8 md:p-10">
            <h2 className="font-heading font-bold text-2xl text-ink">Send us a message</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-ink/70">Name *</label>
                <input data-testid="contact-name-input" className={inputCls + " mt-1.5"} value={form.name} onChange={set("name")} placeholder="Your full name" />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/70">Phone</label>
                <input data-testid="contact-phone-input" className={inputCls + " mt-1.5"} value={form.phone} onChange={set("phone")} placeholder="(416) 000-0000" />
              </div>
            </div>
            <div className="mt-5">
              <label className="text-sm font-medium text-ink/70">Email *</label>
              <input data-testid="contact-email-input" type="email" className={inputCls + " mt-1.5"} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div className="mt-5">
              <label className="text-sm font-medium text-ink/70">Subject</label>
              <input data-testid="contact-subject-input" className={inputCls + " mt-1.5"} value={form.subject} onChange={set("subject")} placeholder="e.g. Office cleaning quote" />
            </div>
            <div className="mt-5">
              <label className="text-sm font-medium text-ink/70">Message *</label>
              <textarea data-testid="contact-message-input" rows={5} className={inputCls + " mt-1.5 resize-none"} value={form.message} onChange={set("message")} placeholder="Tell us about your space and what you need..." />
            </div>
            <button data-testid="contact-submit-btn" disabled={loading} className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-ink text-white font-semibold px-6 py-4 transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-brand disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
